// TenantCard.jsx
import React from "react";
import PropTypes from "prop-types";
import { Button } from "antd";
import { Link } from "react-router-dom";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const TenantCard = ({
  tenant,
  isExpanded = false,
  onToggle,
  showExpandIcon = false,
  variant = "single", // "single", "multi", "collapsed"
  productCount = 0,
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case "single":
        return "bg-blue-50 border border-blue-100 shadow-sm";
      case "multi":
        return "bg-green-50 border border-green-100 shadow-sm";
      case "collapsed":
        return "bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:shadow-sm cursor-pointer";
      default:
        return "bg-gray-50 border border-gray-200";
    }
  };

  const getAvatarClasses = () => {
    switch (variant) {
      case "single":
        return "bg-blue-500 text-white";
      case "multi":
        return "bg-green-500 text-white";
      case "collapsed":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-400 text-white";
    }
  };

  const handleCardClick = () => {
    if (showExpandIcon && onToggle) {
      onToggle();
    }
  };

  return (
    <div
      className={`
        rounded-xl p-3 mb-3  transition-all
        hover:-translate-y-0.5
        ${getVariantClasses()}
      `}
      onClick={handleCardClick}
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div
            className={`
              w-12 h-12 rounded-full flex items-center justify-center
              font-bold text-lg
              shadow-sm
              ${getAvatarClasses()}
            `}
          >
            {getInitials(tenant.name)}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Name + chips row */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="font-semibold text-gray-900 text-base truncate">
              {tenant.name}
            </div>

          
          </div>

          {/* Contact line */}
          <div className="text-xs text-gray-600 flex flex-wrap gap-x-2 gap-y-1">
            {tenant.email && (
              <span className="inline-flex items-center gap-1 truncate">
                <span className="text-[11px]">📧</span>
                <span className="truncate">{tenant.email}</span>
              </span>
            )}
            {tenant.phone && (
              <span className="inline-flex items-center gap-1 truncate">
                <span className="text-[11px]">📞</span>
                <span className="truncate">{tenant.phone}</span>
              </span>
            )}
          </div>

          {/* Address for single variant */}
          {tenant.address && variant === "single" && (
            <div className="text-xs text-gray-600 mt-1 flex items-center gap-1 truncate">
              <span>📍</span>
              <span className="truncate">{tenant.address}</span>
            </div>
          )}
        </div>

        {/* Stats (for multi/collapsed variant) */}
        {(variant === "multi" || variant === "collapsed") && (
          <div className="flex gap-4 items-center flex-shrink-0 ml-2">
            <div className="text-right">
              <div className="text-[11px] text-gray-500 uppercase tracking-wide">
                Products
              </div>
              <div className="text-lg font-bold text-gray-900 leading-tight">
                {productCount}
              </div>
            </div>
            {tenant.revenue !== undefined && (
              <div className="text-right">
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">
                  Revenue
                </div>
                <div className="text-lg font-bold text-green-600 leading-tight">
                  ${tenant.revenue.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manage Tenants Button */}
        <div
          className="flex-shrink-0"
          onClick={(e) => {
            // don't trigger expand/collapse when clicking the button
            e.stopPropagation();
          }}
        >
          <Link to="/tenants">
            <Button size="medium">Manage Tenants</Button>
          </Link>
        </div>

        {/* Expand/Collapse Icon */}
        {showExpandIcon && (
          <span
            className={`
              text-xl flex-shrink-0 ml-1
              ${
                variant === "multi"
                  ? "text-green-500"
                  : variant === "collapsed"
                  ? "text-gray-400"
                  : "text-blue-500"
              }
            `}
          >
            {isExpanded ? "▼" : "▶" }
          </span>
        )}
      </div>
    </div>
  );
};

TenantCard.propTypes = {
  tenant: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    revenue: PropTypes.number,
  }).isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  showExpandIcon: PropTypes.bool,
  variant: PropTypes.oneOf(["single", "multi", "collapsed"]),
  productCount: PropTypes.number,
};

TenantCard.defaultProps = {
  isExpanded: false,
  onToggle: null,
  showExpandIcon: true,
  variant: "single",
  productCount: 0,
};

export default TenantCard;
